import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import { Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { ContentTable } from 'pdfmake/interfaces';
import { useTranslation } from 'react-i18next';
import type { GetStandardFormFieldLabel } from 'src/hooks/forms/useFormCustomisation';
import { UNRATED } from 'src/pages/controls/lookupData';

import { useLazyGetOversightAssessmentRiskAssessmentResultsByRiskId } from '@/hooks/queries';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { toLocalDate } from '@/utils/dateUtils';
import { createTable, tableHeaders } from '@/utils/pdf/table';

const useOversightExportTable = (
  riskId: string,
  getStandardFieldLabel: GetStandardFormFieldLabel
): [() => Promise<ContentTable>, boolean] => {
  const { getLabel: getStatusLabel } = useRating(
    'assessment_status',
    'oversight'
  );
  const { resolveRiskRating, resolveImpact, resolveLikelihood } =
    useRiskRatingResolver('oversight', {
      configType: 'oversight',
    });
  const [getAssessments, getAssessmentsResult] =
    useLazyGetOversightAssessmentRiskAssessmentResultsByRiskId();

  const { t: assessmentsColumns } = useTranslation(['common']);
  const createExportTable = async () => {
    const { data: assessmentsData } = await getAssessments({ riskId });

    const controlledAssessmentsTableData = (
      assessmentsData?.risk_controlled_oversight_result ?? []
    ).map((au) => [
      au.parents?.[0]?.oversightAssessment?.Title ?? '-',
      getStatusLabel(au.parents?.[0]?.oversightAssessment?.Status) ?? '-',
      resolveRiskRating({
        likelihood: au.Likelihood,
        impact: au.Impact,
        controlType: Risk_Assessment_Result_Control_Type_Enum.Controlled,
        rating: au.Rating,
      })?.label ?? UNRATED.label,
      resolveImpact(au.Impact)?.label ?? UNRATED.label,
      resolveLikelihood(au.Likelihood)?.label ?? UNRATED.label,
      toLocalDate(au.parents?.[0]?.oversightAssessment?.StartDate) ?? '-',
      toLocalDate(au.parents?.[0]?.oversightAssessment?.ActualCompletionDate) ??
        '-',
      au.parents?.[0]?.oversightAssessment?.completedByUser?.FriendlyName ??
        '-',
    ]);

    const uncontrolledAssessmentsTableData = (
      assessmentsData?.risk_uncontrolled_oversight_result ?? []
    ).map((au) => [
      au.parents?.[0]?.oversightAssessment?.Title ?? '-',
      getStatusLabel(au.parents?.[0]?.oversightAssessment?.Status) ?? '-',
      resolveRiskRating({
        likelihood: au.Likelihood,
        impact: au.Impact,
        controlType: Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
        rating: au.Rating,
      })?.label ?? UNRATED.label,
      resolveImpact(au.Impact)?.label ?? UNRATED.label,
      resolveLikelihood(au.Likelihood)?.label ?? UNRATED.label,
      toLocalDate(au.parents?.[0]?.oversightAssessment?.StartDate) ?? '-',
      toLocalDate(au.parents?.[0]?.oversightAssessment?.ActualCompletionDate) ??
        '-',
      au.parents?.[0]?.oversightAssessment?.completedByUser?.FriendlyName ??
        '-',
    ]);

    const assessmentsTableData = [
      ...controlledAssessmentsTableData,
      ...uncontrolledAssessmentsTableData,
    ];

    return createTable({
      widths: ['*', 50, 50, 50, 50, 50, 70, 70],
      body: [
        tableHeaders([
          getStandardFieldLabel('oversight_assessment', 'Title'),
          getStandardFieldLabel('oversight_assessment', 'Status'),
          assessmentsColumns('assessmentResults.fields.Rating'),
          assessmentsColumns('assessmentResults.fields.Impact'),
          assessmentsColumns('assessmentResults.fields.Likelihood'),
          getStandardFieldLabel('oversight_assessment', 'StartDate'),
          getStandardFieldLabel('oversight_assessment', 'ActualCompletionDate'),
          getStandardFieldLabel('oversight_assessment', 'CompletedByUser'),
        ]),
        ...assessmentsTableData,
      ],
    });
  };

  return [createExportTable, getAssessmentsResult.loading];
};

export default useOversightExportTable;
